import { createComponent } from '@carvana/merch-components';
import { useCallback, useContext } from 'react';
import { PrimaryButton } from '@/common/Button';
import { Consumer } from '~/constants/common/consumer';
import { useDeviceType } from '~/context/common/DeviceContext';
import { useConsumer } from '~/context/common/StaticContext';
import TradeContext from '~/context/common/TradeContext';
import useGetTradeInStatus from '~/hooks/common/useGetTradeStatus';
import { nativeStcPayload } from '~/native/common/utils';
import { TradeStatus } from './constants';
import DefaultTradeCard from './DefaultTradeCard';
import Details from './Details';
import MultiTradeSelect from './MultiTradeSelect';
import { GreyBar } from './TradeCard.styles';
import TradeHeading from './TradeHeading';
import TradeToggle from './TradeToggle';

const LoadingBlock = createComponent('div', {
  className: 'animate-pulse bg-background-skeleton -:rounded-[6px]'
});

function LoadingCard() {
  return (
    <div
      data-qa="multi-trade-loading-card"
      className="flex w-full flex-col gap-xl rounded-[6px] border border-grey-2 p-2xl"
    >
      <div className="flex justify-between">
        <LoadingBlock className="h-24 w-1/2" />
        <LoadingBlock className="h-24 w-1/3 rounded-full" />
      </div>
      <div className="flex w-full gap-16">
        <LoadingBlock className="h-72 w-72 flex-shrink-0" />
        <div className="flex w-full flex-col gap-8">
          <LoadingBlock className="h-20 w-full" />
          <div className="flex items-baseline justify-between">
            <LoadingBlock className="h-24 w-1/2" />
            <LoadingBlock className="h-20 w-1/4" />
          </div>
        </div>
      </div>
      <GreyBar />
      <div className="flex justify-between">
        <LoadingBlock className="h-24 w-1/2" />
        <LoadingBlock className="h-24 w-1/4 rounded-full" />
      </div>
    </div>
  );
}

type OwnProps = {
  toggleTrade: () => void;
  onMultiTradeSelect?: () => void;
  isHighNetTrade?: boolean;
};

export default function MultiTradeCard({
  toggleTrade,
  onMultiTradeSelect,
  isHighNetTrade = false
}: OwnProps) {
  const tradeStatus = useGetTradeInStatus();
  const {
    selectedTrade,
    trades: { isLoaded }
  } = useContext(TradeContext);
  const { id = '' } = selectedTrade ?? {};
  const { isDesktop } = useDeviceType();
  const handleToggleTrade = useCallback(() => toggleTrade(), [toggleTrade]);
  const consumer = useConsumer();
  const activeToggle = isHighNetTrade
    ? false
    : tradeStatus === TradeStatus.Active ||
      tradeStatus === TradeStatus.ExpiringSoon;

  const ctaProps = {
    dataAnalyticsId: 'add a trade-in',
    dataNativePayload: nativeStcPayload,
    dataQa: 'trade-in-view-offer-cta',
    href: id ? `/sell-my-car/hub/${id}` : '/sell-my-car'
  };

  if (!isLoaded) return <LoadingCard />;

  return selectedTrade ? (
    <div
      data-qa="multi-trade-card"
      className="flex w-full max-w-[680px] flex-col gap-xl rounded-[6px] border border-grey-2 bg-white p-2xl"
    >
      <TradeHeading
        abbreviateTitle={isDesktop}
        isHighNetTrade={isHighNetTrade}
      />
      <Details ctaProps={ctaProps} useEquity />
      {activeToggle && <TradeToggle onToggle={handleToggleTrade} />}
      {tradeStatus === TradeStatus.Expired && (
        <a
          href={`/sell-my-car/hub/${id}`}
          data-qa="trade-card-expired-cta"
          data-analytics-id="trade card expired"
          data-native-payload={nativeStcPayload}
        >
          <PrimaryButton className="min-h-40 w-full">
            Refresh Your Trade
          </PrimaryButton>
        </a>
      )}
      {isHighNetTrade && (
        <>
          <GreyBar />
          <p data-qa="high-net-trade-copy" className="text-blue-6 tl-body-s">
            We can’t apply your trade-in to your purchase because its value is
            higher than the vehicle you’re considering.{' '}
            <a
              href="/help/#contact-flow-module-shell"
              target="_blank"
              data-analytics-id="High Net Trade Support"
              className="text-blue-2 tl-link-s hover:text-blue-3"
            >
              Contact Support
            </a>{' '}
          </p>
        </>
      )}
      <MultiTradeSelect onMultiTradeSelect={onMultiTradeSelect} />
    </div>
  ) : (
    <DefaultTradeCard ctaProps={ctaProps} onVDP={consumer === Consumer.VDP} />
  );
}
